import { Query } from 'react-apollo';
import React, { useEffect } from 'react';

import CoachAvailabilityView from 'coaching/components/CoachAvailabilityView/CoachAvailabilityView';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import CoachingErrorReporter from 'coaching/CoachingErrorReporter';
import EventTracking from 'lib/EventTracking';
import useFullWeekOffset from 'coaching/hooks/useFullWeekOffset';
import GET_COACH_AVAILABILITY_SCHEDULES_AND_AVAILABILITIES_BY_DAY_QUERY from 'coaching/queries/GetCoachAvailabilityData.gql';
import {
  GetCoachAvailabilityData,
  GetCoachAvailabilityData_coaching_getCoachAvailabilitySchedules_schedules,
  GetCoachAvailabilityData_coaching_getCoachAvailabilitySchedules_schedules_availabilityRules,
  GetCoachAvailabilityData_coaching_getCoachAvailabilitySchedules_schedules_maxDailySessions,
  GetCoachAvailabilityData_coaching_getCoachAvailabilitiesByDay_availabilities,
  GetCoachAvailabilityData_coaching_me_coachData_pause,
} from 'coaching/queries/__generated__/GetCoachAvailabilityData';

export type CoachingAvailabilitySchedule = GetCoachAvailabilityData_coaching_getCoachAvailabilitySchedules_schedules;
export type CoachingAvailabilityRule = GetCoachAvailabilityData_coaching_getCoachAvailabilitySchedules_schedules_availabilityRules;
export type CoachingAvailabilityMaxDailySessions = GetCoachAvailabilityData_coaching_getCoachAvailabilitySchedules_schedules_maxDailySessions;

export type SingleDayCoachAvailability = GetCoachAvailabilityData_coaching_getCoachAvailabilitiesByDay_availabilities;
export type CoachPause = GetCoachAvailabilityData_coaching_me_coachData_pause;

export const coachAvailabilityViewEventPrefix = 'coach_availability.coach_availability_view';

export default function CoachAvailabilityContainer() {
  useEffect(() => {
    trackEvent('view');
  }, []);

  function trackEvent(event: string) {
    EventTracking.trackKey(`${coachAvailabilityViewEventPrefix}.${event}`);
  }

  const { advanceWeekOffset, regressWeekOffset, startDate, endDate, state } = useFullWeekOffset();

  return (
    <Query<GetCoachAvailabilityData>
      query={GET_COACH_AVAILABILITY_SCHEDULES_AND_AVAILABILITIES_BY_DAY_QUERY}
      variables={{ startDate, endDate }}
      fetchPolicy="network-only">
      {({ loading, error, data }) => {
        if (loading) {
          return <CenteredLoadingIndicator />;
        } else if (!data?.coaching_getCoachAvailabilitySchedules || error) {
          return (
            <CoachingErrorReporter
              error={error ?? 'Unable to load and render coach availabilities'}
            />
          );
        }

        const { schedules, coachTimezone } = data.coaching_getCoachAvailabilitySchedules;
        const availabilities = data.coaching_getCoachAvailabilitiesByDay?.availabilities ?? [];
        const coachPause = data.coaching_me.coachData?.pause ?? undefined;

        return (
          <CoachAvailabilityView
            schedules={schedules}
            coachTimezone={coachTimezone}
            availabilities={availabilities}
            weekOffset={state.weekOffset}
            onWeekForward={advanceWeekOffset}
            onWeekBack={regressWeekOffset}
            weekStartDate={startDate}
            weekEndDate={endDate}
            availabilityQuery={{
              query: GET_COACH_AVAILABILITY_SCHEDULES_AND_AVAILABILITIES_BY_DAY_QUERY,
              variables: {
                startDate,
                endDate,
              },
            }}
            pause={coachPause}
          />
        );
      }}
    </Query>
  );
}
